// Runtime URL-policy helpers for manifest/source/script security enforcement.
// Defaults stay fail-closed while `data-luis-run` can opt one CDN origin into script loads.
import { resolveRuntimeCoreScriptInfo } from './core-script'
import {
  isRuntimeAssetFilename,
  isSafeRuntimeNetworkUrl,
  isSameOriginRuntimeUrl,
  normalizeRuntimeAllowedOrigins,
  resolveRuntimeSecurityUrl,
} from './url-shared'

export type LuisRuntimeSecurityPolicy = {
  allowRuntimeDiagnostics?: boolean
  allowCrossOriginManifest?: boolean
  allowCrossOriginLuisSources?: boolean
  cdnScriptAllowlist?: string[]
  cdnScriptIntegrity?: Record<string, string>
  cdnScriptIntegrityManifest?: string
  luisSourceIntegrity?: Record<string, string>
  styleNonce?: string
  onSecurityViolation?: (event: LuisRuntimeSecurityViolationEvent) => void
  reportingEndpoint?: string
  reportTo?: string
  maxSecurityEventsPerSession?: number
  securityReportBatchSize?: number
  trustedTypesPolicyName?: string
  maxWorkerPayloadBytes?: number
  maxWorkerCompileTimeMs?: number
}

export type LuisRuntimeSecurityViolationType =
  | 'compiled-css-blocked'
  | 'observed-css-blocked'
  | 'cross-origin-source-rejected'
  | 'integrity-mismatch'
  | 'worker-payload-rejected'
  | 'diagnostics-blocked'
  | 'runtime-script-rejected'

export type LuisRuntimeSecurityViolationEvent = {
  type: LuisRuntimeSecurityViolationType
  timestamp: number
  message: string
  sourceId?: string
  href?: string
  feature?: string
  details?: Record<string, string | number | boolean>
}

type RuntimeSecurityWindow = Window & {
  '__LentyStyleSecurityPolicy'?: LuisRuntimeSecurityPolicy
}

// Exposes the merged browser runtime security policy from script and window hints.
export function readRuntimeSecurityPolicy(): LuisRuntimeSecurityPolicy {
  // Return an empty runtime policy outside browser environments.
  if (typeof window === 'undefined') {
    return {}
  } else {
    // Continue browser policy resolution from runtime globals.
  }

  const runtimeWindow = window as RuntimeSecurityWindow
  return {
    ...buildRuntimeScriptAttributePolicy(),
    ...(runtimeWindow['__LentyStyleSecurityPolicy'] ?? {}),
  }
}

// Exposes whether runtime debug and map diagnostics may activate.
export function isRuntimeDiagnosticsAllowed(): boolean {
  return readRuntimeSecurityPolicy().allowRuntimeDiagnostics === true
}

// Exposes the configured CSP nonce for runtime-created style tags.
export function resolveRuntimeStyleNonce(): string | undefined {
  const nonce = readRuntimeSecurityPolicy().styleNonce?.trim()
  // Return a nonce only when policy provides a non-empty value.
  return nonce || undefined
}

// Exposes whether one manifest URL is allowed by the current runtime policy.
export function isAllowedManifestUrl(rawUrl: string, baseHref: string): boolean {
  const resolved = resolveRuntimeSecurityUrl(rawUrl, baseHref)
  // Reject manifest URLs that cannot be parsed or use blocked protocols.
  if (!resolved || !isSafeRuntimeNetworkUrl(resolved)) {
    return false
  } else {
    // Continue manifest origin validation for one parsed network URL.
  }

  const policy = readRuntimeSecurityPolicy()
  return policy.allowCrossOriginManifest === true || isSameOriginRuntimeUrl(resolved)
}

// Exposes whether one authored initial `.luis` source URL is allowed by policy.
export function isAllowedLuisSourceUrl(rawUrl: string, baseHref: string): boolean {
  const resolved = resolveRuntimeSecurityUrl(rawUrl, baseHref)
  // Reject source URLs that cannot be parsed or use blocked protocols.
  if (!resolved || !isSafeRuntimeNetworkUrl(resolved)) {
    return false
  } else {
    // Continue source origin validation for one parsed network URL.
  }

  const policy = readRuntimeSecurityPolicy()
  // Allow same-origin authored sources without cross-origin integrity policy.
  if (isSameOriginRuntimeUrl(resolved)) {
    return true
  } else {
    // Continue cross-origin source policy checks.
  }

  return policy.allowCrossOriginLuisSources === true && hasLuisSourceIntegrityPin(policy, resolved)
}

// Keep cross-origin source URL admission tied to a concrete integrity pin.
function hasLuisSourceIntegrityPin(policy: LuisRuntimeSecurityPolicy, resolved: URL): boolean {
  return Boolean(policy.luisSourceIntegrity?.[resolved.href]?.trim())
}

// Exposes whether one runtime feature or worker script URL is allowed by policy.
export function isAllowedRuntimeScriptUrl(rawUrl: string, baseHref: string): boolean {
  const resolved = resolveRuntimeSecurityUrl(rawUrl, baseHref)
  // Reject script URLs that are unsafe or not known runtime asset filenames.
  if (!resolved || !isSafeRuntimeNetworkUrl(resolved) || !isRuntimeAssetFilename(resolved)) {
    return false
  } else {
    // Continue runtime script origin validation.
  }

  // Allow same-origin runtime scripts without allowlist checks.
  if (isSameOriginRuntimeUrl(resolved)) {
    return true
  } else {
    // Continue allowlist validation for cross-origin runtime scripts.
  }

  const allowlist = normalizeRuntimeAllowedOrigins(readRuntimeSecurityPolicy().cdnScriptAllowlist)
  return allowlist.has(resolved.origin)
}

// Build runtime script-loading policy hints from the core script tag attributes.
function buildRuntimeScriptAttributePolicy(): LuisRuntimeSecurityPolicy {
  const coreScript = resolveRuntimeCoreScriptInfo()
  // Return no attribute-derived policy when the core script cannot be identified.
  if (!coreScript) {
    return {}
  } else {
    // Continue `data-luis-run` policy derivation for the current core script.
  }

  // Keep the default fail-closed policy when `cdn` is not explicitly requested.
  if (!coreScript.runTokens.includes('cdn')) {
    return {}
  } else {
    // Continue CDN-origin allowlist derivation from the core script URL.
  }

  return {
    cdnScriptAllowlist: [coreScript.origin],
    cdnScriptIntegrityManifest: new URL('lentystyle.integrity.json', coreScript.baseHref).href,
  }
}
